// Staff console. Built for the event-day reality: two people, a camera, and a
// laptop with an SD card reader.
//
// THE SHAPE CHANGED. There is no guest to pick any more. Every photo belongs to
// the BOOTH it was shot at, and the booth is decided by who signed in — so the
// step that used to be "select the guest, and check the green bar matches the
// person who was just shot" is gone, and with it the one mistake that could
// mis-assign a whole batch. An operator opens the console and is already in the
// right place.
//
// Deliberately English-only — it is an operator tool used by a trained pair, and
// mirroring it would cost clarity under pressure.
import {
  type DragEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type StaffAiJob,
  type StaffMedia,
  ApiError,
  createAiJob,
  staffGuestDetail,
  staffHide,
  staffLogin,
  staffReassign,
  staffRerunAi,
  staffWall,
} from "../lib/api";
import { clearSession, saveSession, staffToken, staffUser } from "../lib/session";
import { uploadCounts } from "../lib/db";
import { enqueueMany, onQueueChange, retryFailed, startUploadQueue } from "../lib/uploads";

/**
 * The two permanent booth rows, seeded by migration 0009.
 *
 * These UUIDs are fixed literals in the database and MUST match it: they are
 * the owner of every photo and the first path segment of every storage object.
 * Changing one here without changing the migration orphans a booth's entire
 * gallery.
 */
const BOOTHS = [
  { code: "STANDA", id: "0a17a000-0000-4000-8000-000000000001", label: "Booth A" },
  { code: "STANDB", id: "0a17a000-0000-4000-8000-000000000002", label: "Booth B" },
] as const;

type Booth = (typeof BOOTHS)[number];

const BOOTH_KEY = "laqta.staff.booth";

/** Every staff call reads the token at call time, so a re-login takes effect at once. */
const tok = staffToken;

export default function Staff() {
  const [signedIn, setSignedIn] = useState(() => Boolean(staffToken()));
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (signedIn) return startUploadQueue();
  }, [signedIn]);

  async function submitLogin(e: FormEvent) {
    e.preventDefault();
    if (!username.trim() || !password || busy) return;
    setBusy(true);
    setError(null);
    try {
      saveSession(await staffLogin(username.trim(), password));
      setPassword("");
      setSignedIn(true);
    } catch (err) {
      // The server gives one message for every failure mode on purpose. Show
      // exactly that — guessing "wrong password" vs "no such user" here would
      // hand back the distinction the server spent effort hiding.
      setError(
        err instanceof ApiError && err.status === 401
          ? "Wrong username or password."
          : err instanceof ApiError && err.status === 429
          ? "Too many attempts. Wait a minute."
          : "Cannot reach the server. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!signedIn) {
    return (
      <Shell>
        <div className="mx-auto max-w-sm pt-16">
          <h1 className="text-2xl font-bold">Staff</h1>
          <p className="mt-2 text-sm text-muted">
            Sign in with your own account. Every photo and every portrait records
            who did it.
          </p>
          <form onSubmit={submitLogin} className="mt-6 space-y-4">
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder="Username"
              dir="ltr"
              className="w-full rounded-xl border border-line bg-ink-soft px-4 py-3
                         text-base outline-none placeholder:text-muted focus:border-accent"
            />
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              dir="ltr"
              className="w-full rounded-xl border border-line bg-ink-soft px-4 py-3
                         text-base outline-none placeholder:text-muted focus:border-accent"
            />
            {error && <p role="alert" className="text-sm text-danger">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-xl bg-accent px-4 py-3.5 font-semibold text-ink
                         disabled:opacity-60"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </Shell>
    );
  }

  return (
    <Console
      onLock={() => {
        clearSession();
        setUsername("");
        setPassword("");
        setSignedIn(false);
      }}
    />
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    // dir=ltr regardless of anything else — this is an English tool.
    <div dir="ltr" className="min-h-dvh bg-ink text-paper">
      <div className="mx-auto w-full max-w-6xl px-4 pb-24 pt-5">{children}</div>
    </div>
  );
}

/* --------------------------------- console -------------------------------- */

function Console({ onLock }: { onLock: () => void }) {
  // Read once at mount: signing out unmounts this whole component, so the
  // signed-in account cannot change underneath it.
  const [me] = useState(staffUser);

  // An operator is BOUND to their booth — the binding is enforced in the
  // database (staff_users_operator_needs_booth_check), so there is nothing to
  // choose and nothing to get wrong. An admin works either booth and picks.
  const bound = BOOTHS.find((b) => b.code === me?.boothCode) ?? null;
  const [booth, setBooth] = useState<Booth>(() => {
    if (bound) return bound;
    const saved = sessionStorage.getItem(BOOTH_KEY);
    return BOOTHS.find((b) => b.code === saved) ?? BOOTHS[0];
  });

  const [queue, setQueue] = useState({ pending: 0, failed: 0, synced: 0 });

  const refreshCounts = useCallback(() => {
    void uploadCounts().then(setQueue);
  }, []);

  useEffect(() => {
    refreshCounts();
    return onQueueChange(refreshCounts);
  }, [refreshCounts]);

  function pickBooth(next: Booth) {
    sessionStorage.setItem(BOOTH_KEY, next.code);
    setBooth(next);
  }

  return (
    <Shell>
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold">Staff console</h1>
          {/* Which booth these photos land in. For an operator this is fixed
              and shown as a fact; for an admin it is the one thing they must
              get right before dropping files, so it is a control, not a label. */}
          {bound
            ? (
              <span className="rounded-lg border border-accent bg-accent/10 px-3 py-1.5
                               font-mono text-sm tracking-widest text-accent">
                {bound.code}
              </span>
            )
            : (
              <div className="flex items-center gap-1" role="group" aria-label="Booth">
                {BOOTHS.map((b) => (
                  <button
                    key={b.code}
                    type="button"
                    onClick={() => pickBooth(b)}
                    aria-pressed={b.code === booth.code}
                    className={`rounded-lg border px-3 py-1.5 font-mono text-sm tracking-widest ${
                      b.code === booth.code
                        ? "border-accent bg-accent/10 text-accent"
                        : "border-line text-muted"
                    }`}
                  >
                    {b.code}
                  </button>
                ))}
              </div>
            )}
        </div>

        <div className="flex items-center gap-2">
          {me && (
            <span className="rounded-lg border border-line px-3 py-1.5 text-sm text-muted">
              {me.displayName}
            </span>
          )}
          <a
            href="/wall"
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-line px-3 py-1.5 text-sm text-muted"
          >
            Open wall ↗
          </a>
          <button
            type="button"
            onClick={onLock}
            className="rounded-lg border border-line px-3 py-1.5 text-sm text-muted"
          >
            Sign out
          </button>
        </div>
      </header>

      <QueueBar queue={queue} onRetry={() => void retryFailed()} />

      {/* Keyed by booth so switching REMOUNTS the workspace instead of
          re-rendering it with the previous booth's state still in place.
          Without the key, media and jobs stay on screen under the new booth's
          heading until the fetch returns — and the wall is the one surface
          where approving a photo you have misread cannot be taken back. */}
      <BoothWorkspace key={booth.code} booth={booth} />
    </Shell>
  );
}

/* -------------------------------- queue bar ------------------------------- */

function QueueBar(
  { queue, onRetry }: {
    queue: { pending: number; failed: number; synced: number };
    onRetry: () => void;
  },
) {
  const busy = queue.pending > 0;
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border
                    border-line bg-ink-soft px-4 py-3 text-sm">
      <span className={busy ? "text-accent" : "text-muted"}>
        {busy ? `Uploading ${queue.pending}…` : "Queue empty"}
      </span>
      <span className="text-muted">·</span>
      <span className="text-muted">{queue.synced} sent</span>
      {queue.failed > 0 && (
        <>
          <span className="text-muted">·</span>
          <span className="text-red-400">{queue.failed} failed</span>
          <button
            type="button"
            onClick={onRetry}
            className="ms-auto rounded-lg border border-line px-2.5 py-1 text-xs"
          >
            Retry all
          </button>
        </>
      )}
    </div>
  );
}

/* ------------------------------- the workspace ----------------------------- */

function BoothWorkspace({ booth }: { booth: Booth }) {
  const [media, setMedia] = useState<StaffMedia[]>([]);
  const [jobs, setJobs] = useState<StaffAiJob[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [dragging, setDragging] = useState(false);

  const other = BOOTHS.find((b) => b.code !== booth.code) ?? booth;

  const refresh = useCallback(async () => {
    try {
      const res = await staffGuestDetail(booth.code, tok());
      setMedia(res.media);
      setJobs(res.jobs);
    } catch (e) {
      setNote(String(e).slice(0, 140));
    }
  }, [booth.code]);

  useEffect(() => {
    setSelection(new Set());
    void refresh();
    const t = setInterval(() => void refresh(), 10_000);
    return () => clearInterval(t);
  }, [refresh]);

  const addFiles = useCallback(
    async (files: File[] | FileList | null, kind: "backdrop" | "external") => {
      const list = files ? Array.from(files as ArrayLike<File>) : [];
      const images = list.filter((f) => f.type.startsWith("image/"));
      if (!images.length) {
        if (list.length) setNote("Those files are not images.");
        return;
      }
      try {
        // One durable write for the whole selection. Compression happens in the
        // worker — doing it here would leave the not-yet-reached files alive
        // only in memory, where a refresh destroys them silently.
        await enqueueMany({
          guestId: booth.id,
          guestCode: booth.code,
          kind,
          files: images,
        });
        const skipped = list.length - images.length;
        setNote(
          `${images.length} queued for ${booth.code}` +
            (skipped ? ` · ${skipped} non-image skipped` : ""),
        );
      } catch (e) {
        setNote(`Could not queue: ${String(e).slice(0, 100)}`);
      }
    },
    [booth.id, booth.code],
  );

  // Drag-and-drop straight off the SD card folder — the whole point of the
  // laptop workflow. The drop target is the entire workspace, not a small strip,
  // because aiming at a small target with 40 files selected is miserable.
  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDragging(false);
    void addFiles(e.dataTransfer?.files ?? null, "backdrop");
  }

  const toggleSelect = (id: string) =>
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={`relative mt-5 rounded-xl border p-4 transition-colors ${
        dragging ? "border-accent bg-accent/5" : "border-line"
      }`}
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center
                        justify-center rounded-xl bg-ink/80">
          <p className="text-lg font-semibold text-accent">
            Drop photos for {booth.code}
          </p>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Lane
          title="Camera photos"
          hint="Drag files here, or browse. Queued on device, uploaded in the background."
          onFiles={(f) => void addFiles(f, "backdrop")}
        />
        <Lane
          title="Upload generated image"
          hint="An image made outside this system. It joins the grid like any other photo and can go on the wall."
          onFiles={(f) => void addFiles(f, "external")}
        />
      </div>

      {note && <p className="mt-3 text-sm text-muted">{note}</p>}

      {selection.size > 0 && (
        <MoveBar
          count={selection.size}
          to={other}
          onCancel={() => setSelection(new Set())}
          onMove={async () => {
            try {
              const res = await staffReassign([...selection], other.code, tok());
              setNote(`Moved ${res.moved} photo(s) to ${other.code}`);
              setSelection(new Set());
              void refresh();
            } catch (e) {
              setNote(String(e).slice(0, 160));
            }
          }}
        />
      )}

      <AiJobs
        jobs={jobs}
        onRerun={async (id) => {
          try {
            await staffRerunAi(id, tok());
            setNote("Re-run queued");
            void refresh();
          } catch (e) {
            setNote(String(e).slice(0, 140));
          }
        }}
      />

      <MediaGrid
        media={media}
        selection={selection}
        onToggleSelect={toggleSelect}
        onToggleHide={async (m) => {
          try {
            await staffHide(m.id, m.status !== "hidden", tok());
            void refresh();
          } catch (e) {
            setNote(String(e).slice(0, 140));
          }
        }}
        onToggleWall={async (m) => {
          try {
            const res = await staffWall(m.id, !m.onWall, tok());
            setNote(
              res.onWall
                ? `On the screen — published by ${res.approvedBy ?? "you"}`
                : "Pulled from the screen",
            );
            void refresh();
          } catch (e) {
            setNote(String(e).slice(0, 160));
          }
        }}
        onGenerate={async (m) => {
          try {
            await createAiJob({ guestCode: booth.code, sourceMediaId: m.id }, tok());
            setNote("AI portrait queued from this shot");
            void refresh();
          } catch (e) {
            setNote(String(e).slice(0, 160));
          }
        }}
      />
    </section>
  );
}

/* ---------------------------------- lanes --------------------------------- */

function Lane(
  { title, hint, onFiles }: {
    title: string;
    hint: string;
    onFiles: (files: FileList | null) => void;
  },
) {
  const captureRef = useRef<HTMLInputElement | null>(null);
  const chooseRef = useRef<HTMLInputElement | null>(null);

  return (
    <div className="rounded-xl border border-line bg-ink-soft p-4">
      <h3 className="font-semibold">{title}</h3>
      <p className="mt-1 text-xs text-muted">{hint}</p>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => chooseRef.current?.click()}
          className="flex-1 rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-ink"
        >
          Browse files
        </button>
        <button
          type="button"
          onClick={() => captureRef.current?.click()}
          className="rounded-xl border border-line px-4 py-3 text-sm"
          title="Use this device's camera"
        >
          Camera
        </button>
      </div>

      <input
        ref={captureRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <input
        ref={chooseRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

/* --------------------------------- move bar -------------------------------- */

function MoveBar(
  { count, to, onMove, onCancel }: {
    count: number;
    to: Booth;
    onMove: () => void;
    onCancel: () => void;
  },
) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border
                    border-accent bg-accent/5 px-4 py-3">
      <span className="text-sm">{count} selected</span>
      {/* There are exactly two booths, so "move" has one destination and needs
          no typed code — which removes the typo that reassignment existed to
          fix in the first place. */}
      <button
        type="button"
        onClick={onMove}
        className="rounded-lg bg-accent px-3 py-1.5 text-sm font-semibold text-ink"
      >
        Move to {to.code}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="rounded-lg border border-line px-3 py-1.5 text-sm text-muted"
      >
        Cancel
      </button>
      <span className="w-full text-xs text-muted">
        Moving a photo also pulls it off the wall — approval never transfers.
      </span>
    </div>
  );
}

/* --------------------------------- ai jobs -------------------------------- */

function AiJobs(
  { jobs, onRerun }: { jobs: StaffAiJob[]; onRerun: (id: string) => void },
) {
  if (jobs.length === 0) return null;
  return (
    <div className="mt-5">
      <h3 className="text-xs uppercase tracking-widest text-muted">AI jobs</h3>
      <ul className="mt-2 space-y-2">
        {jobs.map((j) => (
          <li
            key={j.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border
                       border-line px-3 py-2 text-sm"
          >
            <span
              className={j.status === "failed"
                ? "text-red-400"
                : j.status === "done"
                ? "text-accent"
                : "text-muted"}
            >
              {j.status}
            </span>
            {j.attempts > 0 && <span className="text-xs text-muted">×{j.attempts}</span>}
            {j.operator && <span className="text-xs text-muted">{j.operator}</span>}
            {/* Latency is the number that answers "is the model struggling, or
                is it just this one job?" before anyone opens a log. */}
            {j.latencyMs !== null && j.latencyMs !== undefined && (
              <span className="text-xs text-muted">
                {(j.latencyMs / 1000).toFixed(1)}s
              </span>
            )}
            {j.error && (
              <span className="min-w-0 flex-1 truncate text-xs text-muted">{j.error}</span>
            )}
            {j.status === "failed" && (
              <button
                type="button"
                onClick={() => onRerun(j.id)}
                className="ms-auto shrink-0 rounded-lg border border-line px-2.5 py-1 text-xs"
              >
                Re-run
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------- media grid ------------------------------- */

const ORIGIN_BADGE: Record<string, string> = {
  backdrop: "BD",
  ai: "AI",
  external: "EXT",
};

function MediaGrid(
  { media, selection, onToggleSelect, onToggleHide, onToggleWall, onGenerate }: {
    media: StaffMedia[];
    selection: Set<string>;
    onToggleSelect: (id: string) => void;
    onToggleHide: (m: StaffMedia) => void;
    onToggleWall: (m: StaffMedia) => void;
    onGenerate: (m: StaffMedia) => void;
  },
) {
  if (media.length === 0) {
    return (
      <p className="mt-6 text-sm text-muted">
        No photos yet. Drag files anywhere in this panel to add them.
      </p>
    );
  }

  return (
    <div className="mt-5">
      <h3 className="text-xs uppercase tracking-widest text-muted">
        Photos ({media.length}) · click to select for moving
      </h3>
      <ul className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-5">
        {media.map((m) => {
          const chosen = selection.has(m.id);
          return (
            <li key={m.id} className="relative">
              <button
                type="button"
                onClick={() => onToggleSelect(m.id)}
                className={`block w-full overflow-hidden rounded-lg border-2 ${
                  chosen ? "border-accent" : "border-transparent"
                }`}
              >
                {m.thumbUrl ?? m.url
                  ? (
                    <img
                      src={m.thumbUrl ?? m.url ?? ""}
                      alt=""
                      loading="lazy"
                      className={`aspect-square w-full object-cover ${
                        m.status === "hidden" ? "opacity-30" : ""
                      }`}
                    />
                  )
                  : (
                    <div className="flex aspect-square w-full items-center justify-center
                                    rounded-lg border border-line text-[10px] text-muted">
                      {m.status}
                    </div>
                  )}
              </button>

              <span className="absolute start-1 top-1 rounded bg-ink/80 px-1 text-[10px]">
                {ORIGIN_BADGE[m.origin] ?? m.origin}
              </span>
              {m.onWall && (
                <span
                  className="absolute end-1 top-1 rounded bg-accent px-1
                             text-[10px] font-bold text-ink"
                  // Staff approval is the only wall gate, so who approved is
                  // the record of who stands behind it being up there.
                  title={m.operator ? `Published by ${m.operator}` : "On the public screen"}
                >
                  WALL
                </span>
              )}

              <div className="mt-1 flex gap-1">
                {/* Generate from THIS shot. Not offered on an AI output —
                    re-running AI on a generated image compounds artefacts. */}
                {m.origin !== "ai" && m.status === "ready" && (
                  <button
                    type="button"
                    onClick={() => onGenerate(m)}
                    className="flex-1 rounded border border-line py-1 text-[10px]"
                    title="Generate an AI portrait from this shot"
                  >
                    AI
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onToggleWall(m)}
                  className={`flex-1 rounded border py-1 text-[10px] ${
                    m.onWall ? "border-accent text-accent" : "border-line"
                  }`}
                  title="Show on the public screen — look at it first"
                >
                  {m.onWall ? "Pull" : "Wall"}
                </button>
                <button
                  type="button"
                  onClick={() => onToggleHide(m)}
                  className="flex-1 rounded border border-line py-1 text-[10px]"
                >
                  {m.status === "hidden" ? "Show" : "Hide"}
                </button>
              </div>

              {/* Who put this photo here. With two named operators on one
                  laptop, "who uploaded that" is otherwise unanswerable. */}
              {m.operator && !m.onWall && (
                <p className="mt-0.5 truncate text-[9px] text-muted">{m.operator}</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
